import { useMemo } from 'react'

import { ModeEnum, type Mode } from '@web/contexts/appModeContext'

export type DashboardPrivatePendingShieldSlice = {
  processingMultiCount: number | null
  badgeText: string | null
}

export type UseDashboardPrivatePendingRowParams = {
  mode: Mode
  shouldShowBatchOrHint: boolean
  isRailgunBalanceFetching: boolean
  totalMissingInternalPOIUSD: number
  totalMissingExternalPOIUSD: number
  shieldPending: DashboardPrivatePendingShieldSlice
}

export type UseDashboardPrivatePendingRowResult = {
  totalMissingPoiUsd: number
  showPrivatePendingRow: boolean
}

/**
 * Row visibility and missing-POI USD total for the private dashboard overview
 * (shield pending badge + missing POI pill).
 */
export function useDashboardPrivatePendingRow(
  params: UseDashboardPrivatePendingRowParams
): UseDashboardPrivatePendingRowResult {
  const {
    mode,
    shouldShowBatchOrHint,
    isRailgunBalanceFetching,
    totalMissingInternalPOIUSD,
    totalMissingExternalPOIUSD,
    shieldPending
  } = params

  const totalMissingPoiUsd = useMemo(() => {
    if (mode !== ModeEnum.PRIVATE) return 0
    return totalMissingInternalPOIUSD + totalMissingExternalPOIUSD
  }, [mode, totalMissingInternalPOIUSD, totalMissingExternalPOIUSD])

  const showPrivatePendingRow = useMemo(
    () =>
      !shouldShowBatchOrHint &&
      mode === ModeEnum.PRIVATE &&
      !isRailgunBalanceFetching &&
      (shieldPending.processingMultiCount != null ||
        shieldPending.badgeText != null ||
        totalMissingPoiUsd > 0),
    [
      shouldShowBatchOrHint,
      mode,
      isRailgunBalanceFetching,
      shieldPending.processingMultiCount,
      shieldPending.badgeText,
      totalMissingPoiUsd
    ]
  )

  return { totalMissingPoiUsd, showPrivatePendingRow }
}
